/*

localStorage can become null at any time for any reason, such as
storage being full, cookie settings blocking it, or private browsing mode being enabled.

We therefore have to access it safely each time.
*/

import throwError from '../error';

const tryLocalStorageMethod = (methodName: string, ...args: string[]) => {
  try {
    // SSR guard: window object doesn't exist during server-side rendering
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage[methodName](...args);
  } catch (e) {
    // Return null silently for expected localStorage access issues
    // These commonly occur in private browsing, cross-origin contexts, or when localStorage is disabled
    if (e instanceof DOMException && e.name === 'SecurityError') {
      // eslint-disable-next-line no-console
      console.warn(`Error accessing localStorage: ${methodName} ${args} ${e}`);
      return null;
    }
    throwError(`Error accessing localStorage: ${methodName} ${args} ${e}`);
  }
  return null;
};

const safeLocalStorage = {
  getItem(name: string) {
    return tryLocalStorageMethod('getItem', name);
  },
  setItem(name: string, value: string) {
    tryLocalStorageMethod('setItem', name, value);
  },
  removeItem(name: string) {
    tryLocalStorageMethod('removeItem', name);
  },
};

export default safeLocalStorage;
